import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { userService } from '../services/userService';
import { authService } from '../services/authService';
import './Login.css';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCodeMode, setResetCodeMode] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [resetDone, setResetDone] = useState(false);
  const [showPassword, setShowPassword]             = useState(false);
  const [showNewPw, setShowNewPw]                   = useState(false);
  const [showConfirmPw, setShowConfirmPw]           = useState(false);
  const [showResetNewPw, setShowResetNewPw]         = useState(false);
  const [showResetConfirmPw, setShowResetConfirmPw] = useState(false);
  const [otp, setOtp] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [emailNotVerified, setEmailNotVerified] = useState(false);
  const [ssoMode, setSsoMode] = useState(false);
  const [ssoEmail, setSsoEmail] = useState('');
  const [ssoLoading, setSsoLoading] = useState(false);
  const { login, completeNewPassword, verifyMfa, resendMfaCode, verifyCognitoMfa, isAuthenticated, passwordChallenge, mfaChallenge, cognitoMfaChallenge } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [from] = useState(() => {
    const path = location.state?.from?.pathname || sessionStorage.getItem('loginRedirect') || '/dashboard';
    sessionStorage.removeItem('loginRedirect');
    return path;
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setEmailNotVerified(false);
    setLoading(true);
    try {
      const result = await login(username.trim(), password.trim());
      if (result?.requiresMfa || result?.requiresPasswordChange) {
        // challenge state set in AuthContext, Login will re-render
        return;
      }
      if (result?.pendingInvitations?.length > 0) {
        navigate('/invitations', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to login. Please check your credentials.');
      if (err.code === 'EMAILNOTVERIFIED') {
        setEmailNotVerified(true);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    setResendMessage('');
    setError('');
    try {
      await authService.resendVerification(username.trim());
      setResendMessage('Verification email sent. Please check your inbox and try logging in again.');
      setEmailNotVerified(false);
    } catch (err) {
      setError(err.message || 'Failed to send verification email.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    setLoading(true);
    try {
      const result = await completeNewPassword(newPassword.trim());
      if (result?.pendingInvitations?.length > 0) {
        navigate('/invitations', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      console.error('Set password error:', err);
      setError(err.message || 'Failed to set new password.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await authService.forgotPasswordRequest(resetEmail.trim());
      setResetCodeMode(true);
    } catch (err) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    setError('');
    if (resetNewPassword !== resetConfirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await authService.confirmForgotPassword(resetEmail.trim(), resetCode.trim(), resetNewPassword);
      setResetDone(true);
      setResetCodeMode(false);
    } catch (err) {
      setError(err.message || 'Failed to reset password.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendMessage('');
    setError('');
    try {
      await resendMfaCode();
      setOtp('');
      setResendMessage('A new code has been sent to your email.');
    } catch (err) {
      setError(err.message || 'Failed to resend code.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerifyMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await verifyMfa(otp.trim());
      if (result?.pendingInvitations?.length > 0) {
        navigate('/invitations', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCognitoMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await verifyCognitoMfa(otp.trim());
      if (result?.pendingInvitations?.length > 0) {
        navigate('/invitations', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Invalid verification code.');
    } finally {
      setLoading(false);
    }
  };

  if (cognitoMfaChallenge) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Verification Required</h1>
          <p className="subtitle">A verification code has been sent to your email address.</p>
          {error && <div className="error">{error}</div>}
          <form onSubmit={handleVerifyCognitoMfa}>
            <div className="form-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                autoFocus
                disabled={loading}
                placeholder="Enter 6-digit code"
                maxLength={6}
                inputMode="numeric"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (mfaChallenge) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Verification Required</h1>
          <p className="subtitle">A verification code has been sent to your email address.</p>

          {error && <div className="error">{error}</div>}
          {resendMessage && <div className="success">{resendMessage}</div>}

          <form onSubmit={handleVerifyMfa}>
            <div className="form-group">
              <label htmlFor="otp">Verification Code</label>
              <input
                type="text"
                id="otp"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                required
                autoFocus
                disabled={loading}
                placeholder="Enter 6-digit code"
                maxLength={6}
                inputMode="numeric"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={loading || resendLoading}>
              {loading ? 'Verifying...' : 'Verify'}
            </button>
          </form>
          <div className="login-footer">
            <button type="button" className="btn-link" onClick={handleResendCode} disabled={resendLoading || loading}>
              {resendLoading ? 'Sending...' : 'Resend code'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (resetMode) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Reset Password</h1>

          {resetDone ? (
            <>
              <p className="subtitle">Your password has been reset. You can now log in with your new password.</p>
              <button className="btn btn-primary btn-full" onClick={() => { setResetMode(false); setResetDone(false); setResetEmail(''); setResetCode(''); setResetNewPassword(''); setResetConfirmPassword(''); }}>
                Back to Login
              </button>
            </>
          ) : resetCodeMode ? (
            <>
              <p className="subtitle">Enter the 6-digit code sent to {resetEmail} and choose a new password.</p>
              {error && <div className="error">{error}</div>}
              <form onSubmit={handleConfirmReset}>
                <div className="form-group">
                  <label htmlFor="resetCode">Verification Code</label>
                  <input
                    type="text"
                    id="resetCode"
                    value={resetCode}
                    onChange={(e) => setResetCode(e.target.value)}
                    required
                    autoFocus
                    disabled={loading}
                    placeholder="6-digit code"
                    maxLength={6}
                    inputMode="numeric"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="resetNewPassword">New Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showResetNewPw ? 'text' : 'password'}
                      id="resetNewPassword"
                      value={resetNewPassword}
                      onChange={(e) => setResetNewPassword(e.target.value)}
                      required
                      disabled={loading}
                      placeholder="Enter new password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowResetNewPw(v => !v)} tabIndex={-1} aria-label={showResetNewPw ? 'Hide password' : 'Show password'}>
                      {showResetNewPw
                        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="resetConfirmPassword">Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showResetConfirmPw ? 'text' : 'password'}
                      id="resetConfirmPassword"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      required
                      disabled={loading}
                      placeholder="Confirm new password"
                    />
                    <button type="button" className="password-toggle" onClick={() => setShowResetConfirmPw(v => !v)} tabIndex={-1} aria-label={showResetConfirmPw ? 'Hide password' : 'Show password'}>
                      {showResetConfirmPw
                        ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                    </button>
                  </div>
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </form>
              <div className="login-footer">
                <button type="button" className="btn-link" onClick={() => { setResetCodeMode(false); setError(''); }} disabled={loading}>
                  Back
                </button>
              </div>
            </>
          ) : (
            <>
              <p className="subtitle">Enter your email and we'll send you a reset code.</p>
              {error && <div className="error">{error}</div>}
              <form onSubmit={handleResetPassword}>
                <div className="form-group">
                  <label htmlFor="resetEmail">Email</label>
                  <input
                    type="email"
                    id="resetEmail"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    required
                    autoFocus
                    disabled={loading}
                    placeholder="your.email@example.com"
                  />
                </div>
                <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </button>
              </form>
              <div className="login-footer">
                <button type="button" className="btn-link" onClick={() => { setResetMode(false); setError(''); }}>
                  Back to Login
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  if (passwordChallenge) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>Set New Password</h1>
          <p className="subtitle">You must set a new password before continuing.</p>

          {error && <div className="error">{error}</div>}

          <form onSubmit={handleSetPassword}>
            <div className="form-group">
              <label htmlFor="newPassword">New Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showNewPw ? 'text' : 'password'}
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  autoFocus
                  disabled={loading}
                  placeholder="Enter new password"
                />
                <button type="button" className="password-toggle" onClick={() => setShowNewPw(v => !v)} tabIndex={-1} aria-label={showNewPw ? 'Hide password' : 'Show password'}>
                  {showNewPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="confirmPassword">Confirm Password</label>
              <div className="password-input-wrapper">
                <input
                  type={showConfirmPw ? 'text' : 'password'}
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  placeholder="Confirm new password"
                />
                <button type="button" className="password-toggle" onClick={() => setShowConfirmPw(v => !v)} tabIndex={-1} aria-label={showConfirmPw ? 'Hide password' : 'Show password'}>
                  {showConfirmPw
                    ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                    : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
                </button>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
              {loading ? 'Saving...' : 'Set Password'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  const handleSsoEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const email = ssoEmail.trim().toLowerCase();
    const parts = email.split('@');
    if (parts.length !== 2 || !parts[1]) {
      setError('Please enter a valid email address.');
      return;
    }
    const domain = parts[1];
    setSsoLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/sso/domain?domain=${encodeURIComponent(domain)}`);
      if (!response.ok) {
        if (response.status === 404) {
          setError('No SSO provider is configured for this email domain. Please sign in with email and password.');
        } else {
          setError('Unable to look up SSO provider. Please try again.');
        }
        setSsoLoading(false);
        return;
      }
      const data = await response.json();
      window.location.href = data.loginUrl;
    } catch {
      setError('Could not contact the server. Please try again.');
      setSsoLoading(false);
    }
  };

  if (ssoMode) {
    return (
      <div className="login-container">
        <div className="login-box">
          <h1>SSO Sign In</h1>
          <p className="subtitle">Enter your work email to be redirected to your organisation's login page.</p>

          {error && <div className="error">{error}</div>}

          <form onSubmit={handleSsoEmailSubmit}>
            <div className="form-group">
              <label htmlFor="ssoEmail">Work Email</label>
              <input
                type="email"
                id="ssoEmail"
                value={ssoEmail}
                onChange={(e) => setSsoEmail(e.target.value)}
                required
                autoFocus
                disabled={ssoLoading}
                placeholder="you@yourcompany.com"
              />
            </div>
            <button type="submit" className="btn btn-primary btn-full" disabled={ssoLoading}>
              {ssoLoading ? 'Redirecting…' : 'Continue with SSO'}
            </button>
          </form>
          <div className="login-footer">
            <button type="button" className="btn-link" onClick={() => { setSsoMode(false); setError(''); setSsoEmail(''); }}>
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-box">
        <div className="login-brand">
          <div className="login-brand-logo">
            <svg width="36" height="36" viewBox="0 0 32 32" fill="none">
              <rect width="32" height="32" rx="8" fill="#3b6ef5"/>
              <rect x="5" y="5" width="10" height="10" rx="1.5" fill="white"/>
              <rect x="17" y="5" width="10" height="10" rx="1.5" fill="white" opacity="0.6"/>
              <rect x="5" y="17" width="10" height="10" rx="1.5" fill="white" opacity="0.6"/>
              <rect x="17" y="17" width="10" height="10" rx="1.5" fill="white"/>
            </svg>
          </div>
          <div className="login-brand-name">Hub <span>by HConnect</span></div>
        </div>
        <div className="login-product-pills">
          <span className="login-pill login-pill-vault">Vault</span>
          <span className="login-pill login-pill-sync">Sync</span>
          <span className="login-pill login-pill-apps">Apps</span>
        </div>
        <p className="subtitle">Sign in to continue</p>

        {error && <div className="error">{error}</div>}
        {resendMessage && <div className="success">{resendMessage}</div>}

        {emailNotVerified && (
          <div className="login-footer" style={{ marginBottom: '16px' }}>
            <button type="button" className="btn-link" onClick={handleResendVerification} disabled={resendLoading || loading}>
              {resendLoading ? 'Sending...' : 'Request new verification email'}
            </button>
          </div>
        )}

        <div className="sso-icon-row">
          <button type="button" className="btn-sso-icon" onClick={() => { setSsoMode(true); setError(''); }} disabled={loading}>
            <span className="sso-icon-wrap">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
              </svg>
            </span>
            <span className="sso-icon-label">SSO</span>
          </button>
        </div>

        <div className="sso-divider"><span>or sign in with email</span></div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Email</label>
            <input
              type="email"
              id="username"
              placeholder="your.email@example.com"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoFocus
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <button type="button" className="password-toggle" onClick={() => setShowPassword(v => !v)} tabIndex={-1} aria-label={showPassword ? 'Hide password' : 'Show password'}>
                {showPassword
                  ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                  : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>
        <div className="login-footer">
          <button type="button" className="btn-link" onClick={() => { setResetMode(true); setError(''); }}>
            Forgot password?
          </button>
        </div>
      </div>
    </div>
  );
};

export default Login;
